import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c205Api, FC130, FC131, FC132 } from '../../../services/c205Api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFieldErrors } from '../../../hooks/useFieldErrors';

export type Mode = 'browse' | 'add' | 'edit';

export function useC205FormState() {
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>('browse');
  const isEditing = mode !== 'browse';
  const [record, setRecord] = useState<FC130 | null>(null);
  const [formData, setFormData] = useState<Partial<FC130>>({});
  const [details, setDetails] = useState<Partial<FC131>[]>([]);
  const [boardDetails, setBoardDetails] = useState<Partial<FC132>[]>([]);
  const [selectedRow, setSelectedRow] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);
  const { fieldErrors, setFieldErrors, clearFieldErrors, getFieldError, hasErrors } = useFieldErrors();

  /** Load a single record by ID */
  const loadRecord = useCallback(async (id: string): Promise<{
    data: FC130;
    dets: Partial<FC131>[];
    boards: Partial<FC132>[];
  } | null> => {
    setLoading(true);
    try {
      const data = await c205Api.get(id);
      setLastApiResponse(data);
      setRecord(data);
      setFormData({ ...data });
      const dets = data.details || [];
      const boards = data.board_details || [];
      setDetails(dets);
      setBoardDetails(boards);
      setSelectedRow(0);
      return { data, dets, boards };
    } catch {
      message.error('載入失敗');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  /** Set a single master field */
  const setField = useCallback((key: keyof FC130, value: FC130[keyof FC130]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  }, []);

  /** Get a single master field value */
  const getField = useCallback((key: keyof FC130): FC130[keyof FC130] => {
    return (formData as Record<string, FC130[keyof FC130]>)[key] ?? '';
  }, [formData]);

  /** Get a detail field value from the selected row */
  const getDetailField = useCallback((key: keyof FC131): FC131[keyof FC131] => {
    const sel = details[selectedRow] as Partial<FC131> | undefined;
    return sel ? (sel as Record<string, FC131[keyof FC131]>)[key] ?? '' : '';
  }, [details, selectedRow]);

  /** Set a detail field value on the selected row */
  const setDetailField = useCallback((key: keyof FC131, value: FC131[keyof FC131]) => {
    setDetails(prev => {
      const next = [...prev];
      if (next[selectedRow]) {
        next[selectedRow] = { ...next[selectedRow], [key]: value };
      }
      return next;
    });
  }, [selectedRow]);

  /** Save (create or update) */
  const handleSave = useCallback(async (
    onSuccess: (result: FC130) => void,
  ) => {
    setLoading(true);
    clearFieldErrors();
    try {
      const req = {
        master: formData as FC130,
        details: details as FC131[],
        board_details: boardDetails as FC132[],
      };
      if (mode === 'add') {
        const result = await c205Api.create(req);
        setLastApiResponse(result);
        message.success(`新增成功: ${result.fc130_01}`);
        onSuccess(result);
      } else {
        const result = await c205Api.update(formData.fc130_01!, req);
        setLastApiResponse(result);
        message.success('修改成功');
        setRecord(result);
        setFormData({ ...result });
        setDetails(result.details || []);
        setBoardDetails(result.board_details || []);
        onSuccess(result);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { errors?: { field: string; message: string }[]; error?: string } } };
      const errors = err.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        setFieldErrors(errors);
        message.error('請修正標示的欄位');
      } else {
        message.error(err.response?.data?.error || '儲存失敗');
      }
    } finally {
      setLoading(false);
    }
  }, [formData, details, boardDetails, mode, clearFieldErrors, setFieldErrors]);

  /** Delete the current record */
  const handleDelete = useCallback(async (): Promise<boolean> => {
    if (!record) return false;
    try {
      await c205Api.delete(record.fc130_01);
      setLastApiResponse({ deleted: record.fc130_01 });
      message.success('刪除成功');
      return true;
    } catch {
      message.error('刪除失敗');
      return false;
    }
  }, [record]);

  /** Add a new detail row */
  const addDetailRow = useCallback(() => {
    const maxSeq = details.reduce((max, d) => Math.max(max, d.fc131_02 || 0), 0);
    setDetails(prev => [...prev, { fc131_02: maxSeq + 1, fc131_01: formData.fc130_01 || '' }]);
  }, [details, formData.fc130_01]);

  /** Delete the currently selected detail row */
  const deleteDetailRow = useCallback(() => {
    if (details.length <= 0) return;
    setDetails(prev => {
      const next = prev.filter((_, i) => i !== selectedRow);
      return next.map((d, i) => ({ ...d, fc131_02: i + 1 }));
    });
    setSelectedRow(Math.max(0, selectedRow - 1));
  }, [details.length, selectedRow]);

  /** Reset form to current record (revert) */
  const revertToRecord = useCallback(() => {
    clearFieldErrors();
    if (record) {
      setFormData({ ...record });
      setDetails(record.details || []);
      setBoardDetails(record.board_details || []);
    }
  }, [record, clearFieldErrors]);

  /** Reset form for a new add */
  const resetForAdd = useCallback(() => {
    clearFieldErrors();
    setRecord(null);
    setFormData({
      fc130_17: 1,
      fc130_19: 0,
      fc130_20: 1,
      fc130_80: 'N',
      fc130_91: user?.userId || '',
    });
    setDetails([]);
    setBoardDetails([]);
    setSelectedRow(0);
  }, [user, clearFieldErrors]);

  /** Apply result after save */
  const applyResult = useCallback((result: FC130) => {
    setRecord(result);
    setFormData({ ...result });
    setDetails(result.details || []);
    setBoardDetails(result.board_details || []);
  }, []);

  return {
    mode, setMode, isEditing,
    record, formData, details, boardDetails, selectedRow, loading,
    setSelectedRow, setDetails, setBoardDetails,
    loadRecord, setField, getField,
    getDetailField, setDetailField,
    handleSave, handleDelete,
    addDetailRow, deleteDetailRow,
    revertToRecord, resetForAdd, applyResult,
    fieldErrors, getFieldError, clearFieldErrors, hasErrors,
    lastApiResponse, setLastApiResponse,
  };
}
